import React, { useEffect, useRef, useState } from 'react';
import { AspectRatio, Flex, Spinner } from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Text, Card, Button } from 'components';
import { handOffDuoCallback, takePendingDuoLogin, DUO_DENIED_MESSAGE } from 'utils';
import { GPPayLogoLogin } from 'assets';

import { loginDuo, getDuoLoginError, startSession } from './session';

const INCOMPLETE_MESSAGE = 'Your sign in could not be completed. Please sign in again.';

// Duo sends the browser back here after the prompt, with the code to exchange and the state we gave it.
// In the popup flow this page hands the result to the login page that opened it; in the redirect flow
// (popup blocked) it finishes the sign in itself
export const DuoCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>();
  // Set when this popup passed the result to the login page and is closing
  const [handedOff, setHandedOff] = useState(false);

  // The code can only be exchanged once, so never send it twice
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const state = searchParams.get('state') || undefined;
    const duoCode = searchParams.get('duo_code') || searchParams.get('code') || undefined;
    const error = searchParams.get('error') || undefined;
    const errorDescription = searchParams.get('error_description') || undefined;

    const pending = takePendingDuoLogin();

    // No redirect login in this tab, so this is the Duo popup. Hand the result to the
    // login page that opened it, which finishes the sign in there
    if (!pending) {
      handOffDuoCallback({ state, duoCode, error, errorDescription }).then((taken) => {
        if (taken) {
          setHandedOff(true);
          window.close();
          return;
        }
        setErrorMessage(INCOMPLETE_MESSAGE);
      });
      return;
    }

    if (error) {
      setErrorMessage(errorDescription ?? DUO_DENIED_MESSAGE);
      return;
    }
    if (!duoCode || !state) {
      setErrorMessage(INCOMPLETE_MESSAGE);
      return;
    }

    loginDuo({ PreAuthToken: pending.preAuthToken, State: state, DuoCode: duoCode }).then(
      (result: any) => {
        if (result.error) {
          setErrorMessage(getDuoLoginError(result));
          return;
        }
        const sessionError = startSession(result, (to) => navigate(to, { replace: true }));
        if (sessionError) {
          setErrorMessage(sessionError);
        }
      },
    );
  }, [searchParams, navigate]);

  return (
    <Flex h={'100vh'} w={'100vw'} align={'center'} justify={'center'} backgroundColor={'#FFF'}>
      <div>
        <AspectRatio w={'286.5px'} h={'48px'} mb={'32px'}>
          <img src={GPPayLogoLogin} alt={'GuestPoint Pay Logo'} />
        </AspectRatio>
        <Card
          size="sm"
          width="453px"
          filter={
            'drop-shadow(-2px -2px 8px rgba(0, 0, 0, 0.08)) drop-shadow(2px 2px 8px rgba(0, 0, 0, 0.08))'
          }
          boxShadow={'none'}
          p={'32px'}
        >
          <Text variant="heading04Bold" mb={4}>
            {errorMessage ? 'Sign In Failed' : 'Completing Sign In'}
          </Text>
          {errorMessage ? (
            <>
              <Text variant="pMediumRegular" mb={6}>
                {errorMessage}
              </Text>
              <Button
                colorScheme="accent"
                label={'Back to sign in'}
                variant="solid"
                width="100%"
                onClick={() => navigate('/', { replace: true })}
              />
            </>
          ) : handedOff ? (
            // Shown if the browser refuses to let this popup close itself
            <Text variant="pMediumRegular">
              {'Duo verification is complete. You can close this window.'}
            </Text>
          ) : (
            <Flex align={'center'} gap={2}>
              <Spinner size="sm" />
              <Text variant="pMediumRegular">{'Checking your Duo verification…'}</Text>
            </Flex>
          )}
        </Card>
      </div>
    </Flex>
  );
};
